import { SectionSelectField } from "@/components/SectionSelectField";
import { saveErrorMessage } from "@/lib/saveErrorMessage";
import { PricingTreatmentField } from "@/components/PricingTreatmentField";
import { MarkupOverrideField } from "@/components/MarkupOverrideField";
import { calculateItemTotalCost } from "@/lib/itineraryCostUtils";
import { pricingTreatment, type PricingTreatment } from "@/lib/quoteChoices";

import { RichTextField } from "@/components/RichTextField";
import { richTextLength } from "@/lib/richText";
import { useState, useEffect, useMemo, useRef } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { z } from "zod";
import { format } from "date-fns";
import { DatePickerField } from "./DatePickerField";
import { SessionScheduleField } from "./SessionScheduleField";
import { DEFAULT_SCHEDULE, scheduleFromItem, scheduleToPayload, type SessionSchedule } from "@/lib/sessionSchedule";
import { calculateAccommodationNights, calculateUniversalFeeDays } from "@/lib/itineraryCostUtils";
import { ImageUploadField } from "./ImageUploadField";
import { UtensilsCrossed, Bus, Palmtree, Coffee, Hotel, Compass, Plane, Ticket, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDatePickerDefault } from "@/hooks/itinerary-dates";
import { ItemInclusionsExclusionsFields, arrayToLines, linesToArray } from "./ItemInclusionsExclusionsFields";
import { defaultSectionForItem } from "@/lib/itinerarySections";
import { RoomAllocationsEditor } from "./itinerary/RoomAllocationsEditor";
import { type RoomAllocation, parseAllocations, mirrorFields, copyMix, nightlyTotal } from "@/lib/roomAllocations";


const DEFAULT_IMAGES = [
  { id: "free-time", label: "Free Time", icon: Palmtree, color: "text-blue-500" },
  { id: "meal", label: "Meal", icon: UtensilsCrossed, color: "text-orange-500" },
  { id: "transfer", label: "Transfer", icon: Bus, color: "text-green-500" },
  { id: "other", label: "Other", icon: Coffee, color: "text-purple-500" },
  { id: "accommodation", label: "Accommodation", icon: Hotel, color: "text-cyan-500" },
  { id: "tours", label: "Tours", icon: Compass, color: "text-emerald-500" },
  { id: "flights", label: "Flights", icon: Plane, color: "text-sky-500" },
  { id: "activities", label: "Activities", icon: Ticket, color: "text-pink-500" },
];

const itemSchema = z.object({
  title: z.string().min(1, "Title is required").max(200),
  item_type: z.enum(["accommodation", "transfer", "activity", "meal", "flight", "language_excursions", "conference", "tour", "custom", "insurance", "supplier", "universal_fee"]),
  date: z.date().optional(),
  check_in: z.date().optional(),
  check_out: z.date().optional(),
  service_start: z.date().optional(),
  service_end: z.date().optional(),
  accommodation_type: z.string().optional(),
  location: z.string().max(200).optional(),
  description: z.string().max(6000, "Description is too long").refine((v) => richTextLength(v) <= 2500, "Description must be less than 2500 characters").optional(),
  cost: z.number().min(0, "Cost must be positive").optional(),
  quantity: z.number().int().min(1, "Quantity must be at least 1").optional(),
  supplier: z.string().max(200).optional(),
  notes: z.string().max(2500, "Notes must be less than 2500 characters").optional(),
  website_url: z.string().max(500).optional(),
 universal_fee_kind: z.enum(["portage", "guiding", "assistant_guide", "st", "staff_hotel", "chef", "fejo", "custom"]).optional(),
  staff_categories: z.array(z.string()).optional(),
  room_guest_type: z.enum(["guest", "staff"]).optional(),
  staff_role_tags: z.array(z.string()).optional(),
});

type ItineraryItem = {
  id: string;
  title: string;
  custom_title: string | null;
  custom_description: string | null;
  item_type: string;
  date: string | null;
  check_in: string | null;
  check_out: string | null;
  service_start?: string | null;
  service_end?: string | null;
  accommodation_type: string | null;
  occupancy_type: string | null;
  room_rate_id: string | null;
  location: string | null;
  description: string | null;
  cost: number | null;
  manual_price: number | null;
  quantity: number | null;
  supplier: string | null;
  notes: string | null;
  website_url?: string | null;
  manual_image_url: string | null;
  is_background_cost?: boolean | null;
  universal_fee_kind?: string | null;
  staff_categories?: string[] | null;
  room_guest_type?: string | null;
  staff_role_tags?: string[] | null;
  section_name?: string | null;
  markup_percentage?: number | null;

};

interface EditItineraryItemDialogProps {
  item: ItineraryItem | null;
  leadId: string;
  leadStatus?: string;
  leadStartDate?: string | null;
  sectionOptions?: string[];
  onClose: () => void;
}

export function EditItineraryItemDialog({ item, leadId, leadStatus, leadStartDate, sectionOptions = [], onClose }: EditItineraryItemDialogProps) {
  // Single source of truth for itinerary default date.
  // Only used to seed/`defaultMonth` when the item has no existing date.
  const defaultDate = useDatePickerDefault(leadStartDate);
  // Tracks explicit user date selection; gates the async adoption effect.
  const userHasPicked = useRef(false);
  const [schedule, setSchedule] = useState<SessionSchedule>({ ...DEFAULT_SCHEDULE });
  const [formData, setFormData] = useState<{
    title: string;
    item_type: "accommodation" | "transfer" | "activity" | "meal" | "flight" | "language_excursions" | "conference" | "tour" | "custom" | "insurance" | "supplier" | "universal_fee";
    date: Date | undefined;
    check_in: Date | undefined;
    check_out: Date | undefined;
    service_start: Date | undefined;
    service_end: Date | undefined;
    accommodation_type: string;
    location: string;
    description: string;
    cost: string;
    quantity: string;
    supplier: string;
    notes: string;
    website_url: string;
    
    selectedDefaultImage: string;
    start_time: string;
    end_time: string;
    is_background_cost: boolean;
    universal_fee_kind: "" | "portage" | "guiding" | "assistant_guide" | "st" | "staff_hotel" | "chef" | "fejo" | "custom";
    staff_categories: string[];
    room_guest_type: "guest" | "staff";
    staff_role_tags: string[];
    section_name: string;
  }>({
    title: "",
    item_type: "accommodation",
    date: undefined,
    check_in: undefined,
    check_out: undefined,
    service_start: undefined,
    service_end: undefined,
    accommodation_type: "",
    location: "",
    description: "",
    cost: "",
    quantity: "1",
    supplier: "",
    notes: "",
    website_url: "",
    selectedDefaultImage: "",
    start_time: "",
    end_time: "",
    is_background_cost: false,
    universal_fee_kind: "",
    staff_categories: [],
    room_guest_type: "guest",
    staff_role_tags: [],
    section_name: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pricing, setPricing] = useState<PricingTreatment>("included");
  const [alternativeTo, setAlternativeTo] = useState<string | null>(null);
  const [linkedOption, setLinkedOption] = useState<string | null>(null);
  const [markupOverride, setMarkupOverride] = useState<string>("");

  const { data: leadMarkupRow } = useQuery({
    queryKey: ["lead-markup", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("markup_percentage")
        .eq("id", leadId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!leadId,
  });
  const leadMarkupPct = leadMarkupRow?.markup_percentage ?? null;

  const editedItemNetTotal = useMemo(() => {
    if (!item) return 0;
    const cost = parseFloat(formData.cost) || 0;
    return calculateItemTotalCost({
      ...(item as any),
      item_type: formData.item_type,
      cost,
      manual_price: cost,
      quantity: parseInt(formData.quantity) || 1,
      check_in: formData.check_in ? formData.check_in.toISOString().slice(0, 10) : null,
      check_out: formData.check_out ? formData.check_out.toISOString().slice(0, 10) : null,
    } as any);
  }, [item, formData.cost, formData.quantity, formData.item_type, formData.check_in, formData.check_out]);




  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [secondaryImage, setSecondaryImage] = useState<string | null>(null);
  const [image3, setImage3] = useState<string | null>(null);
  const [image4, setImage4] = useState<string | null>(null);
  const [selectedRoomRateId, setSelectedRoomRateId] = useState<string | null>(null);
  const [occupancyType, setOccupancyType] = useState<"single" | "double" | "triple">("double");
  const [allocations, setAllocations] = useState<RoomAllocation[]>([]);
  const [guestCount, setGuestCount] = useState<number>(1);
  const [inclusionsText, setInclusionsText] = useState("");
  const [exclusionsText, setExclusionsText] = useState("");
  const [savingDefaults, setSavingDefaults] = useState(false);
  const queryClient = useQueryClient();


  // Fetch lead pax count to pre-populate villa guest count
  const [customBasis, setCustomBasis] = useState<"flat" | "per_person">("flat");
  const { data: leadPax } = useQuery({
    queryKey: ["lead-pax-count", leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("pax_count")
        .eq("id", leadId)
        .single();
      if (error) throw error;
      return data?.pax_count ?? 1;
    },
    enabled: !!leadId,
  });

  // Get rate based on occupancy type
  const getRateByOccupancy = (roomRate: any, occType: "single" | "double" | "triple"): number => {
    if (!roomRate) return 0;
    switch (occType) {
      case "single": return roomRate.single_rate || 0;
      case "triple": return roomRate.triple_rate || roomRate.double_rate || 0;
      default: return roomRate.double_rate || 0;
    }
  };

  // Fetch supplier categories
  const { data: categories } = useQuery({
    queryKey: ["supplier-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("supplier_categories")
        .select("*")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  // Fetch suppliers based on selected accommodation type
  const { data: suppliers } = useQuery({
    queryKey: ["suppliers-by-category", formData.accommodation_type],
    queryFn: async () => {
      if (!formData.accommodation_type) return [];
      
      const categoryMap: Record<string, string> = {
        "hotels": "Hotels",
        "guesthouses": "Guesthouses",
        "safari_lodges": "Safari Lodges",
        "beach_lodges": "Beach Lodges",
        "hostels": "Hostels",
        "retreat_lodges": "Retreat Lodge",
        "villas": "Villas",
      };
      
      const categoryName = categoryMap[formData.accommodation_type];
      const category = categories?.find(c => c.name === categoryName);
      
      if (!category) return [];
      
      const { data, error } = await supabase
        .from("suppliers")
        .select("*")
        .eq("category_id", category.id)
        .order("name");
      
      if (error) throw error;
      return data;
    },
    enabled: !!formData.accommodation_type && formData.item_type === "accommodation",
  });

  // Find the selected supplier's ID from their name
  const selectedSupplierId = useMemo(() => {
    if (!suppliers || !formData.supplier) return null;
    const found = suppliers.find(s => s.name === formData.supplier);
    return found?.id || null;
  }, [suppliers, formData.supplier]);

  // Fetch room rates for selected accommodation supplier
  const { data: supplierRoomRates } = useQuery({
    queryKey: ["supplier-room-rates", selectedSupplierId],
    queryFn: async () => {
      if (!selectedSupplierId) return [];
      const { data, error } = await supabase
        .from("supplier_room_rates")
        .select("*")
        .eq("supplier_id", selectedSupplierId)
        .eq("is_active", true)
        .order("room_type");
      if (error) throw error;
      return data;
    },
    enabled: !!selectedSupplierId && formData.item_type === "accommodation",
  });

  // Where "save as default" should write back to: the linked experience or the named supplier.
  const saveDefaultsTarget = useMemo(() => {
    const productId = (item as any)?.product_id as string | null | undefined;
    if (productId) return { kind: "product" as const, id: productId };
    const supplierName = (item as any)?.supplier as string | null | undefined;
    if (supplierName && supplierName.trim()) return { kind: "supplier" as const, name: supplierName.trim() };
    return null;
  }, [item]);

  const handleSaveDefaults = async () => {
    if (!saveDefaultsTarget) return;
    const inc = linesToArray(inclusionsText);
    const exc = linesToArray(exclusionsText);
    const payload = {
      default_inclusions: inc.length ? inc : null,
      default_exclusions: exc.length ? exc : null,
    };
    setSavingDefaults(true);
    try {
      if (saveDefaultsTarget.kind === "product") {
        const { error } = await supabase
          .from("products")
          .update(payload)
          .eq("id", saveDefaultsTarget.id);
        if (error) throw error;
      } else {
        const { data: match, error: findError } = await supabase
          .from("suppliers")
          .select("id")
          .ilike("name", saveDefaultsTarget.name)
          .limit(1)
          .maybeSingle();
        if (findError) throw findError;
        if (!match) {
          toast.error("Couldn't find that supplier to save the defaults to");
          return;
        }
        const { error } = await supabase.from("suppliers").update(payload).eq("id", match.id);
        if (error) throw error;
      }
      toast.success("Saved as the default inclusions and exclusions");
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["suppliers"] });
    } catch (e: any) {
      toast.error(e?.message || "Could not save the defaults");
    } finally {
      setSavingDefaults(false);
    }
  };


  useEffect(() => {
    if (item) {
      // Opening (or switching) an item resets the user-picked flag.
      userHasPicked.current = false;
      setSchedule(scheduleFromItem(item));
      const isCustomItem = item.item_type === 'custom';
      if (isCustomItem) setCustomBasis(((item as any).custom_price_basis === 'per_person' || (item.quantity ?? 1) > 1) ? 'per_person' : 'flat');
      const existingImage = item.manual_image_url;
      const isDefaultIcon = existingImage?.startsWith('default-');
      const existingTimeSlot = (item as any).selected_time_slot as { start?: string; end?: string; label?: string } | null;
      
      setPricing(pricingTreatment(item as any));
      setAlternativeTo((item as any).alternative_to_item_id || null);
      setLinkedOption((item as any).linked_option_item_id || null);
      setFormData({
        title: item.custom_title || item.title || "",
        item_type: item.item_type as any,
        // Existing item.date / check_in / check_out always win.
        // Only seed from the centralised hook when the item has no date.
        date: item.date ? new Date(item.date) : defaultDate,
        check_in: item.check_in ? new Date(item.check_in) : defaultDate,
        check_out: item.check_out ? new Date(item.check_out) : defaultDate,
        service_start: item.service_start ? new Date(item.service_start) : undefined,
        service_end: item.service_end ? new Date(item.service_end) : undefined,
        accommodation_type: item.accommodation_type || "",
        location: item.location || "",
        description: item.custom_description || item.description || "",
        cost: isCustomItem 
          ? (item.manual_price?.toString() || "")
          : (item.cost?.toString() || ""),
        quantity: item.quantity?.toString() || "1",
        supplier: item.supplier || "",
        notes: item.notes || "",
        website_url: (item as any).website_url || "",
    selectedDefaultImage: isDefaultIcon ? existingImage.replace('default-', '') : "",
        // Times can live in selected_time_slot or the override columns; read both.
        start_time: (existingTimeSlot?.start || (item as any).override_start_time || "").slice(0, 5),
        end_time: (existingTimeSlot?.end || (item as any).override_end_time || "").slice(0, 5),
        is_background_cost: item.is_background_cost === true,
        universal_fee_kind: (item.universal_fee_kind as any) || "",
        staff_categories: item.staff_categories || [],
        room_guest_type: (item.room_guest_type === "staff" ? "staff" : "guest"),
        staff_role_tags: item.staff_role_tags || [],
        section_name: item.section_name || defaultSectionForItem(item as any),
      });

      setInclusionsText(arrayToLines((item as any).inclusions));
      setMarkupOverride(
        (item as any).markup_percentage === null || (item as any).markup_percentage === undefined
          ? ""
          : String((item as any).markup_percentage)
      );

      setExclusionsText(arrayToLines((item as any).exclusions));
      setUploadedImage(isDefaultIcon ? null : existingImage);
      setSecondaryImage((item as any).secondary_image_url || null);
      setImage3((item as any).image_url_3 || null);
      setImage4((item as any).image_url_4 || null);

      
      // Initialize room rate and occupancy from item
      if (item.room_rate_id) {
        setSelectedRoomRateId(item.room_rate_id);
      }
      if (item.occupancy_type) {
        setOccupancyType(item.occupancy_type as "single" | "double" | "triple");
      }
      // Seed villa guest count: existing quantity if present, else lead pax, else 1
      const isVillaItem = item.item_type === 'accommodation' && item.accommodation_type === 'villas';
      if (isVillaItem) {
        setGuestCount(item.quantity ?? leadPax ?? 1);
      }
    }
  }, [item, defaultDate, leadPax]);

  useEffect(() => {
    setAllocations(parseAllocations((item as any)?.room_allocations));
  }, [item]);

  // Keep the visible cost / rooms fields in step with the room lines.
  useEffect(() => {
    if (allocations.length === 0) return;
    const m = mirrorFields(allocations);
    setFormData((prev) => ({ ...prev, cost: String(Math.round(m.cost * 100) / 100), quantity: String(m.quantity) }));
  }, [allocations]);

  const startMixedRooms = () => {
    const r = (supplierRoomRates as any[])?.find((x) => x.id === selectedRoomRateId);
    const first: RoomAllocation = {
      room_rate_id: selectedRoomRateId,
      room_type: r?.room_type ?? "",
      occupancy: occupancyType,
      rooms: Math.max(1, parseInt(String(formData.quantity)) || 1),
      rate: parseFloat(String(formData.cost)) || 0,
      currency: r?.currency ?? null,
    };
    const second: RoomAllocation = { ...first, occupancy: occupancyType === "single" ? "double" : "single", rooms: 1, rate: getRateByOccupancy(r, occupancyType === "single" ? "double" : "single") || first.rate };
    setAllocations([first, second]);
  };

  const copyMixFromMain = async () => {
    if (!alternativeTo) return;
    const { data } = await supabase
      .from("itinerary_items")
      .select("room_allocations, quantity, occupancy_type, cost")
      .eq("id", alternativeTo)
      .maybeSingle();
    if (!data) return;
    let src = parseAllocations((data as any).room_allocations);
    if (src.length === 0) {
      src = [{ room_rate_id: null, room_type: "", occupancy: ((data as any).occupancy_type || "double") as any, rooms: (data as any).quantity || 1, rate: (data as any).cost || 0 }];
    }
    setAllocations(copyMix(src, ((supplierRoomRates as any[]) || [])));
  };

  // Adopt the hook value when leadStartDate resolves after async load —
  // but only for fields the existing item never had a date for, and only
  // if the user has not picked a date themselves.
  useEffect(() => {
    if (!item) return;
    if (userHasPicked.current) return;
    if (!defaultDate) return;
    setFormData((prev) => ({
      ...prev,
      date: prev.date ?? (item.date ? prev.date : defaultDate),
      check_in: prev.check_in ?? (item.check_in ? prev.check_in : defaultDate),
      check_out: prev.check_out ?? (item.check_out ? prev.check_out : defaultDate),
    }));
  }, [item, defaultDate]);

  const updateItemMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const { error } = await supabase
        .from("itinerary_items")
        .update(data)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["itinerary-items", leadId] });
      queryClient.invalidateQueries({ queryKey: ["itinerary-changes", leadId] });
      toast.success("Item updated successfully!");
      onClose();
    },
    onError: (error) => {
      toast.error(saveErrorMessage(error, "Failed to update item"));
      console.error(error);
    },
  });

  const logChangeMutation = useMutation({
    mutationFn: async (changeData: any) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from("itinerary_changes").insert([
        {
          ...changeData,
          user_id: user.id,
        },
      ]);
      if (error) throw error;
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // GUARD CLAUSE - Prevent editing cancelled leads
    if (leadStatus === 'cancelled') {
      toast.error("Cannot edit items on a cancelled lead");
      onClose();
      return;
    }

    if (!item) return;

    try {
      const validatedData = itemSchema.parse({
        title: formData.title,
        item_type: formData.item_type,
        date: formData.date,
        check_in: formData.check_in,
        check_out: formData.check_out,
        service_start: formData.service_start,
        service_end: formData.service_end,
        accommodation_type: formData.accommodation_type || undefined,
        location: formData.location || undefined,
        description: formData.description || undefined,
        cost: formData.cost ? parseFloat(formData.cost) : undefined,
        quantity: formData.quantity ? parseInt(formData.quantity) : 1,
        supplier: formData.supplier || undefined,
        notes: formData.notes || undefined,
        website_url: formData.website_url || undefined,
        universal_fee_kind: formData.universal_fee_kind || undefined,
        staff_categories: formData.staff_categories.length ? formData.staff_categories : undefined,
        room_guest_type: formData.item_type === 'accommodation' ? formData.room_guest_type : undefined,
        staff_role_tags: formData.item_type === 'accommodation' && formData.room_guest_type === 'staff' && formData.staff_role_tags.length
          ? formData.staff_role_tags
          : undefined,
      });

      if (formData.item_type === "universal_fee") {
        if (!formData.universal_fee_kind) {
          setErrors({ universal_fee_kind: "Please select a fee kind" });
          toast.error("Please select a fee kind");
          return;
        }
        if (
          (formData.universal_fee_kind === "st" || formData.universal_fee_kind === "staff_hotel") &&
          formData.staff_categories.length === 0
        ) {
          setErrors({ staff_categories: "Select at least one staff category" });
          toast.error("Select at least one staff category");
          return;
        }
        if (
          formData.universal_fee_kind === "guiding" || formData.universal_fee_kind === "assistant_guide" ||
          formData.universal_fee_kind === "st" ||
          formData.universal_fee_kind === "chef"
        ) {
          if (!formData.service_start || !formData.service_end) {
            setErrors({ service_start: "Start and end date are required" });
            toast.error("Please pick a start and end date");
            return;
          }
          if (formData.service_end < formData.service_start) {
            setErrors({ service_end: "End date must be on or after start date" });
            toast.error("End date must be on or after start date");
            return;
          }
        }
      }

      const isCustomItem = item.item_type === 'custom';
      const costValue = validatedData.cost || 0;

      // For accommodation items, date must equal check_in for correct sorting.
      // Guide / S&T fees are programme-wide and have no date.
      const isAccommodationItem = validatedData.item_type === 'accommodation';
      const isInfraFee =
        validatedData.item_type === 'universal_fee' &&
        (formData.universal_fee_kind === 'guiding' || formData.universal_fee_kind === 'assistant_guide' ||
          formData.universal_fee_kind === 'st' ||
          formData.universal_fee_kind === 'chef' ||
          formData.universal_fee_kind === 'fejo');
      const effectiveDate = isInfraFee
        ? null
        : isAccommodationItem && validatedData.check_in
          ? format(validatedData.check_in, 'yyyy-MM-dd')
          : (validatedData.date ? format(validatedData.date, 'yyyy-MM-dd') : null);

      const updateData: Record<string, any> = {
        title: validatedData.title,
        custom_title: isCustomItem ? validatedData.title : null,
        custom_description: isCustomItem ? (validatedData.description || null) : null,
        item_type: validatedData.item_type,
        date: effectiveDate,
        ...(!isAccommodationItem && !isInfraFee ? scheduleToPayload(schedule, validatedData.date) : {}),
        check_in: validatedData.check_in ? format(validatedData.check_in, 'yyyy-MM-dd') : null,
        check_out: validatedData.check_out ? format(validatedData.check_out, 'yyyy-MM-dd') : null,
        service_start: isInfraFee && validatedData.service_start
          ? format(validatedData.service_start, 'yyyy-MM-dd')
          : null,
        service_end: isInfraFee && validatedData.service_end
          ? format(validatedData.service_end, 'yyyy-MM-dd')
          : null,
        accommodation_type: validatedData.accommodation_type || null,
        location: validatedData.location || null,
        description: isCustomItem ? null : (validatedData.description || null),
        cost: isCustomItem ? null : costValue,
        manual_price: isCustomItem ? costValue : null,
        ...(isCustomItem ? { custom_price_basis: customBasis } : {}),
        quantity: isVilla
          ? guestCount
          : (validatedData.item_type === 'universal_fee' && formData.universal_fee_kind === 'chef'
              ? 1
              : (validatedData.quantity || 1)),
        supplier: validatedData.supplier || null,
        notes: validatedData.notes || null,
        website_url: (validatedData as any).website_url || null,
        manual_image_url: isCustomItem 
          ? (uploadedImage || (formData.selectedDefaultImage ? `default-${formData.selectedDefaultImage}` : null))
          : (validatedData.item_type === 'universal_fee' ? (uploadedImage || null) : null),
        secondary_image_url: secondaryImage || null,
        image_url_3: image3 || null,
        image_url_4: image4 || null,
        selected_time_slot: formData.start_time ? {
          label: "Custom",
          start: formData.start_time,
          end: formData.end_time || formData.start_time,
        } : null,
        // The time shown in the form is now the single source; clear legacy overrides.
        override_start_time: null,
        override_end_time: null,
        // Save room rate and occupancy for accommodation
        room_rate_id: isAccommodationItem ? (selectedRoomRateId || null) : null,
        occupancy_type: isAccommodationItem ? occupancyType : null,
        ...(isAccommodationItem && !isVilla && allocations.length > 0
          ? {
              room_allocations: allocations as any,
              ...mirrorFields(allocations),
              room_rate_id: allocations[0].room_rate_id,
              occupancy_type: allocations[0].occupancy,
            }
          : { room_allocations: null }),
        is_background_cost: formData.is_background_cost,
        pricing_treatment: pricing === "alternative" && !alternativeTo ? "included" : pricing,
        alternative_to_item_id: pricing === "alternative" ? alternativeTo : null,
        linked_option_item_id: pricing === "optional" ? null : linkedOption,
        markup_percentage:
          markupOverride.trim() === "" || !Number.isFinite(parseFloat(markupOverride))
            ? null
            : parseFloat(markupOverride),

        inclusions: linesToArray(inclusionsText).length ? linesToArray(inclusionsText) : null,
        exclusions: linesToArray(exclusionsText).length ? linesToArray(exclusionsText) : null,

        universal_fee_kind: validatedData.item_type === 'universal_fee' ? (formData.universal_fee_kind || null) : null,
        staff_categories: validatedData.item_type === 'universal_fee' &&
          (formData.universal_fee_kind === 'st' || formData.universal_fee_kind === 'staff_hotel')
          ? formData.staff_categories
          : null,
        room_guest_type: isAccommodationItem ? formData.room_guest_type : 'guest',
        staff_role_tags: isAccommodationItem && formData.room_guest_type === 'staff'
          ? (formData.staff_role_tags.length ? formData.staff_role_tags : null)
          : null,
        section_name: formData.section_name.trim() || defaultSectionForItem({ item_type: validatedData.item_type } as any),
      };

      // Log changes for each modified field
      const changes = [];
      if (item.title !== updateData.title) {
        changes.push({
          itinerary_item_id: item.id,
          field_changed: "title",
          old_value: item.title,
          new_value: updateData.title,
          change_source: "Edit",
        });
      }
      // Track price changes based on item type
      const oldPrice = item.item_type === 'custom' ? item.manual_price : item.cost;
      const newPrice = updateData.manual_price || updateData.cost;
      
      if (oldPrice !== newPrice) {
        changes.push({
          itinerary_item_id: item.id,
          field_changed: item.item_type === 'custom' ? "manual_price" : "cost",
          old_value: oldPrice?.toString() || null,
          new_value: newPrice?.toString() || null,
          change_source: "Edit",
        });
      }
      if (item.supplier !== updateData.supplier) {
        changes.push({
          itinerary_item_id: item.id,
          field_changed: "supplier",
          old_value: item.supplier,
          new_value: updateData.supplier,
          change_source: "Edit",
        });
      }

      await updateItemMutation.mutateAsync({ id: item.id, data: updateData });

      for (const change of changes) {
        await logChangeMutation.mutateAsync(change);
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0].toString()] = err.message;
          }
        });
        setErrors(newErrors);
        toast.error("Please fix the validation errors");
      }
    }
  };

  const isAccommodation = formData.item_type === "accommodation";
  const isUniversalFee = formData.item_type === "universal_fee";
  const isDayBased = isUniversalFee && (formData.universal_fee_kind === "guiding" || formData.universal_fee_kind === "assistant_guide" || formData.universal_fee_kind === "st" || formData.universal_fee_kind === "staff_hotel" || formData.universal_fee_kind === "chef");
  const isInfrastructureFeeKind = isUniversalFee && (formData.universal_fee_kind === "guiding" || formData.universal_fee_kind === "assistant_guide" || formData.universal_fee_kind === "st" || formData.universal_fee_kind === "chef" || formData.universal_fee_kind === "fejo");
  const isMultiDayService = isUniversalFee && (formData.universal_fee_kind === "guiding" || formData.universal_fee_kind === "assistant_guide" || formData.universal_fee_kind === "st" || formData.universal_fee_kind === "chef");
  const isVilla = isAccommodation && formData.accommodation_type === "villas";

  return (
    <Dialog open={!!item} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Itinerary Item</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="item_type">Item Type *</Label>
              <Select
                value={formData.item_type}
                onValueChange={(value: any) => {
                  setFormData({ ...formData, item_type: value, accommodation_type: "", supplier: "" });
                }}
                disabled={item?.item_type === 'custom'}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="accommodation">Accommodation</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                  <SelectItem value="activity">Activity</SelectItem>
                  <SelectItem value="meal">Meal</SelectItem>
                  <SelectItem value="flight">Flight</SelectItem>
                  <SelectItem value="language_excursions">Language Excursions</SelectItem>
                  <SelectItem value="conference">Conference</SelectItem>
                  <SelectItem value="tour">Tour</SelectItem>
                  <SelectItem value="insurance">Insurance</SelectItem>
                  <SelectItem value="supplier">Supplier</SelectItem>
                  <SelectItem value="universal_fee">Universal Fee</SelectItem>
                  {item?.item_type === 'custom' && <SelectItem value="custom">Custom</SelectItem>}
                </SelectContent>
              </Select>
              {item?.item_type === 'custom' && (
                <p className="text-xs text-muted-foreground">
                  Custom items from Costings cannot change type
                </p>
              )}
              {errors.item_type && <p className="text-sm text-destructive">{errors.item_type}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
              {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
            </div>
          </div>

          <SectionSelectField
            id="section_name"
            leadId={(item as any)?.lead_id}
            value={formData.section_name || ""}
            onChange={(v) => setFormData((prev: any) => ({ ...prev, section_name: v }))}
            options={sectionOptions}
            hint="Used when this programme is displayed in sections."
          />

          {/* Universal fee fields */}
          {isUniversalFee && (
            <>
              <div className="space-y-2">
                <Label htmlFor="universal_fee_kind">Fee Kind *</Label>
                <Select
                  value={formData.universal_fee_kind}
                  onValueChange={(value: "portage" | "guiding" | "assistant_guide" | "st" | "staff_hotel" | "chef" | "fejo" | "custom") =>
                    setFormData({
                      ...formData,
                      universal_fee_kind: value,
                      cost: value === "fejo" && !formData.cost ? "4600" : formData.cost,
                      staff_categories:
                        value === "st"
                          ? formData.staff_categories
                          : value === "staff_hotel"
                          ? formData.staff_categories.slice(0, 1)
                          : [],
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select fee kind" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portage">Portage Fees</SelectItem>
                    <SelectItem value="guiding">Guiding Fees</SelectItem>
                    <SelectItem value="assistant_guide">Assistant Guide Fees</SelectItem>
                    <SelectItem value="st">S&amp;T (Subsistence &amp; Transport)</SelectItem>
                    <SelectItem value="staff_hotel">Staff Hotel</SelectItem>
                    <SelectItem value="chef">Private Chef</SelectItem>
                    <SelectItem value="fejo">FEJO Consulting Fee</SelectItem>
                    <SelectItem value="custom">Custom Fee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isMultiDayService && (
                <div className="grid grid-cols-2 gap-4">
                  <DatePickerField
                    label="Start Date *"
                    value={formData.service_start}
                    onChange={(date) => {
                      userHasPicked.current = true;
                      setFormData({ ...formData, service_start: date });
                    }}
                    defaultMonth={formData.service_start ?? defaultDate}
                  />
                  <DatePickerField
                    label="End Date *"
                    value={formData.service_end}
                    onChange={(date) => {
                      userHasPicked.current = true;
                      setFormData({ ...formData, service_end: date });
                    }}
                    disabled={(date) => formData.service_start ? date < formData.service_start : false}
                    defaultMonth={formData.service_start ?? defaultDate}
                  />
                  {errors.service_start && (
                    <p className="text-sm text-destructive col-span-2">{errors.service_start}</p>
                  )}
                  {errors.service_end && (
                    <p className="text-sm text-destructive col-span-2">{errors.service_end}</p>
                  )}
                </div>
              )}
              {isMultiDayService && formData.service_start && formData.service_end && (() => {
                const days = calculateUniversalFeeDays(
                  formData.service_start.toISOString(),
                  formData.service_end.toISOString()
                );
                const cost = parseFloat(formData.cost) || 0;
                const isChef = formData.universal_fee_kind === "chef";
                const qty = isChef ? 1 : (parseInt(formData.quantity) || 1);
                const staffMult = formData.universal_fee_kind === "st" ? Math.max(1, formData.staff_categories.length) : 1;
                const total = cost * days * qty * staffMult;
                return days > 0 ? (
                  <div className="p-3 bg-muted/50 rounded-lg text-sm">
                    <span className="font-medium">Total: </span>
                    {cost.toLocaleString()} × {days} {days === 1 ? "day" : "days"}
                    {!isChef ? ` × ${qty}` : ""}
                    {formData.universal_fee_kind === "st" && staffMult > 1 ? ` × ${staffMult} categories` : ""}
                    <span className="font-semibold"> = {total.toLocaleString()}</span>
                  </div>
                ) : null;
              })()}
              {formData.universal_fee_kind === "st" && (
                <div className="space-y-2">
                  <Label>Staff Categories *</Label>
                  <div className="flex flex-wrap gap-4 rounded-md border p-3">
                    {(["driver", "guide", "assistant_guide", "other"] as const).map((cat) => {
                      const checked = formData.staff_categories.includes(cat);
                      return (
                        <label key={cat} className="flex items-center gap-2 text-sm capitalize cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...formData.staff_categories, cat]
                                : formData.staff_categories.filter((c) => c !== cat);
                              setFormData({ ...formData, staff_categories: next });
                            }}
                          />
                          {cat === "assistant_guide" ? "Assistant Guide" : cat}
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Total = cost × days × number of staff categories selected.
                  </p>
                </div>
              )}
              {formData.universal_fee_kind === "staff_hotel" && (
                <div className="space-y-2">
                  <Label>Staff Category *</Label>
                  <div className="flex flex-wrap gap-4 rounded-md border p-3">
                    {(["driver", "guide", "other"] as const).map((cat) => {
                      const checked = formData.staff_categories[0] === cat;
                      return (
                        <label key={cat} className="flex items-center gap-2 text-sm capitalize cursor-pointer">
                          <input
                            type="radio"
                            name="staff_hotel_category"
                            checked={checked}
                            onChange={() =>
                              setFormData({ ...formData, staff_categories: [cat] })
                            }
                          />
                          {cat}
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    One line per staff category. Add another Staff Hotel item for additional staff. Total = cost × nights.
                  </p>
                  {errors.staff_categories && (
                    <p className="text-sm text-destructive">{errors.staff_categories}</p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Accommodation-specific fields */}
          {isAccommodation && (
            <>
              {/* Guest vs Staff room toggle */}
              <div className="space-y-2 rounded-md border p-3 bg-muted/30">
                <Label>Who is this room for?</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={formData.room_guest_type === "guest" ? "default" : "outline"}
                    onClick={() => setFormData({ ...formData, room_guest_type: "guest", staff_role_tags: [] })}
                  >
                    Guest rooms
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={formData.room_guest_type === "staff" ? "default" : "outline"}
                    onClick={() => setFormData({ ...formData, room_guest_type: "staff" })}
                  >
                    Staff rooms
                  </Button>
                </div>
                {formData.room_guest_type === "staff" && (
                  <div className="space-y-2 pt-2">
                    <Label className="text-xs text-muted-foreground">Staff role tags (optional)</Label>
                    <div className="flex flex-wrap gap-4">
                      {(["driver", "guide", "other"] as const).map((cat) => {
                        const checked = formData.staff_role_tags.includes(cat);
                        return (
                          <label key={cat} className="flex items-center gap-2 text-sm capitalize cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...formData.staff_role_tags, cat]
                                  : formData.staff_role_tags.filter((c) => c !== cat);
                                setFormData({ ...formData, staff_role_tags: next });
                              }}
                            />
                            {cat}
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Staff rooms cost is independent of guest pax and won't scale when participants change.
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="accommodation_type">Accommodation Type</Label>
                <Select
                  value={formData.accommodation_type}
                  onValueChange={(value) => setFormData({ ...formData, accommodation_type: value, supplier: "" })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select accommodation type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hotels">Hotels</SelectItem>
                    <SelectItem value="guesthouses">Guesthouses</SelectItem>
                    <SelectItem value="safari_lodges">Safari Lodges</SelectItem>
                    <SelectItem value="beach_lodges">Beach Lodges</SelectItem>
                    <SelectItem value="hostels">Hostels</SelectItem>
                    <SelectItem value="retreat_lodges">Retreat Lodges</SelectItem>
                    <SelectItem value="villas">Villas</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <DatePickerField
                  label="Check In"
                  value={formData.check_in}
                  onChange={(date) => {
                    userHasPicked.current = true;
                    setFormData({ ...formData, check_in: date });
                  }}
                  defaultMonth={formData.check_in ?? defaultDate}
                />

                <DatePickerField
                  label="Check Out"
                  value={formData.check_out}
                  onChange={(date) => {
                    userHasPicked.current = true;
                    setFormData({ ...formData, check_out: date });
                  }}
                  disabled={(date) => formData.check_in ? date < formData.check_in : false}
                  defaultMonth={formData.check_in ?? defaultDate}
                />
              </div>
            </>
          )}

          {/* Supplier dropdown for accommodation */}
          {isAccommodation && formData.accommodation_type && (
            <div className="space-y-2">
              <Label htmlFor="supplier">Supplier</Label>
              <Select
                value={formData.supplier}
                onValueChange={(value) => {
                  setFormData({ ...formData, supplier: value });
                  setSelectedRoomRateId(null);
                  setOccupancyType("double");
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers?.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.name}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                  {(!suppliers || suppliers.length === 0) && (
                    <SelectItem value="no-suppliers" disabled>No suppliers found</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Room Type Selection */}
          {isAccommodation && !isVilla && allocations.length === 0 && selectedSupplierId && supplierRoomRates && supplierRoomRates.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="room_type">Room Type</Label>
              <Select
                value={selectedRoomRateId || ""}
                onValueChange={(value) => {
                  const selectedRate = supplierRoomRates.find(r => r.id === value);
                  setSelectedRoomRateId(value);
                  const rate = getRateByOccupancy(selectedRate, occupancyType);
                  setFormData(prev => ({
                    ...prev,
                    cost: rate > 0 ? rate.toString() : prev.cost,
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select room type" />
                </SelectTrigger>
                <SelectContent>
                  {supplierRoomRates.map((rate) => (
                    <SelectItem key={rate.id} value={rate.id}>
                      {rate.room_type} — {rate.currency} {(rate.double_rate || rate.single_rate || 0).toLocaleString()}/night
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Occupancy Type Selection */}
          {isAccommodation && !isVilla && allocations.length === 0 && selectedRoomRateId && supplierRoomRates && supplierRoomRates.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="occupancy">Occupancy</Label>
              <Select
                value={occupancyType}
                onValueChange={(value: "single" | "double" | "triple") => {
                  setOccupancyType(value);
                  const selectedRate = supplierRoomRates?.find(r => r.id === selectedRoomRateId);
                  const rate = getRateByOccupancy(selectedRate, value);
                  setFormData(prev => ({
                    ...prev,
                    cost: rate > 0 ? rate.toString() : prev.cost,
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem 
                    value="single" 
                    disabled={!supplierRoomRates?.find(r => r.id === selectedRoomRateId)?.single_rate}
                  >
                    Single Occupancy
                  </SelectItem>
                  <SelectItem value="double">Double Occupancy</SelectItem>
                  <SelectItem 
                    value="triple" 
                    disabled={!supplierRoomRates?.find(r => r.id === selectedRoomRateId)?.triple_rate}
                  >
                    Triple Occupancy
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Several room types on one hotel card */}
          {isAccommodation && !isVilla && (
            allocations.length > 0 ? (
              <RoomAllocationsEditor
                lines={allocations}
                onChange={setAllocations}
                rates={(supplierRoomRates as any[]) || []}
                nights={formData.check_in && formData.check_out ? calculateAccommodationNights(formData.check_in as any, formData.check_out as any) : 0}
                onCopyFromMain={pricing === "alternative" && alternativeTo ? copyMixFromMain : undefined}
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={startMixedRooms}>
                  + Add another room type (e.g. singles and doubles on one card)
                </Button>
                {pricing === "alternative" && alternativeTo && (
                  <Button type="button" variant="outline" size="sm" onClick={copyMixFromMain}>
                    Copy room mix from main hotel
                  </Button>
                )}
              </div>
            )
          )}

          {/* General date field for non-accommodation items (exclude insurance and infrastructure fees) */}
          {!isAccommodation && formData.item_type !== "insurance" && !isInfrastructureFeeKind && (
            <>
              <DatePickerField
                label="Date"
                value={formData.date}
                onChange={(date) => {
                  userHasPicked.current = true;
                  setFormData({ ...formData, date });
                }}
                defaultMonth={formData.date ?? defaultDate}
              />
              <SessionScheduleField startDate={formData.date} value={schedule} onChange={setSchedule} />

              {/* Time Slot Editor */}
              <div className="space-y-3 rounded-md border border-border p-3">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Custom Time Override (optional)</span>
                </div>
                <p className="text-xs text-muted-foreground -mt-1">
                  Override the product's default time with a custom start and end time.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_time">Start Time</Label>
                    <Input
                      id="start_time"
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_time">End Time</Label>
                    <Input
                      id="end_time"
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            />
            {errors.location && <p className="text-sm text-destructive">{errors.location}</p>}
          </div>

          {/* Additional Images (Optional) - available for all item types */}
          <div className="space-y-2">
            <Label className="mb-2 block">Additional Images (Optional, up to 3)</Label>
            <p className="text-xs text-muted-foreground">Shown below the hero image in quotes. Leave empty to use the product or supplier photos.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <ImageUploadField value={secondaryImage} onChange={(url) => setSecondaryImage(url)} entityType="product" />
              <ImageUploadField value={image3} onChange={(url) => setImage3(url)} entityType="product" />
              <ImageUploadField value={image4} onChange={(url) => setImage4(url)} entityType="product" />
            </div>
          </div>

          {/* Hero image uploader for universal fees (no default-icon picker) */}
          {item?.item_type === 'universal_fee' && (
            <div className="space-y-2">
              <Label className="mb-2 block">Hero Image (Optional)</Label>
              <p className="text-xs text-muted-foreground">Upload an image to represent this fee in quotes</p>
              <ImageUploadField
                value={uploadedImage}
                onChange={(url) => setUploadedImage(url)}
                entityType="product"
              />
            </div>
          )}

          {/* Image section for custom items */}
          {item?.item_type === 'custom' && (
            <div className="space-y-4">
              <div>
                <Label className="mb-2 block">Hero Image</Label>
                <ImageUploadField
                  value={uploadedImage}
                  onChange={(url) => {
                    setUploadedImage(url);
                    setFormData({ ...formData, selectedDefaultImage: "" });
                  }}
                  entityType="product"
                />
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or select a default icon
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3">
                {DEFAULT_IMAGES.map((img) => {
                  const Icon = img.icon;
                  const isSelected = formData.selectedDefaultImage === img.id;
                  return (
                    <button
                      key={img.id}
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, selectedDefaultImage: img.id });
                        setUploadedImage(null);
                      }}
                      className={cn(
                        "flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all",
                        isSelected
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      <Icon className={cn("h-6 w-6", img.color)} />
                      <span className="text-xs text-muted-foreground">{img.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Supplier for non-accommodation items */}
          {!isAccommodation && !isUniversalFee && (
            <div className="space-y-2">
              <Label htmlFor="supplier">Supplier</Label>
              <Input
                id="supplier"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
              />
              {errors.supplier && <p className="text-sm text-destructive">{errors.supplier}</p>}
            </div>
          )}

          {/* Accommodation pricing info */}
          {isAccommodation && formData.check_in && formData.check_out && (
            (() => {
              const nights = calculateAccommodationNights(
                formData.check_in?.toISOString(),
                formData.check_out?.toISOString()
              );
              const costPerNight = parseFloat(formData.cost) || 0;
              const rooms = isVilla ? 1 : (parseInt(formData.quantity) || 1);
              const total = costPerNight * nights * rooms;
              const lines = allocations.filter((l) => l.rooms > 0);
              if (nights > 0 && !isVilla && lines.length > 0) {
                const mixTotal = nightlyTotal(lines) * nights;
                return (
                  <div className="p-3 bg-muted/50 rounded-lg text-sm">
                    <span className="font-medium">Total: </span>
                    {nights} night{nights > 1 ? 's' : ''} × (
                    {lines.map((l, i) => (
                      <span key={i}>
                        {i > 0 && ' + '}
                        {l.rooms} {l.occupancy.charAt(0).toUpperCase() + l.occupancy.slice(1)} × {l.rate.toLocaleString()}
                      </span>
                    ))}
                    )
                    <span className="font-semibold"> = {mixTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                );
              }
              return nights > 0 ? (
                <div className="p-3 bg-muted/50 rounded-lg text-sm">
                  <span className="font-medium">Total: </span>
                  {nights} night{nights > 1 ? 's' : ''} × {costPerNight.toLocaleString()} per night
                  {!isVilla && occupancyType && (
                    <span className="text-muted-foreground"> ({occupancyType.charAt(0).toUpperCase() + occupancyType.slice(1)})</span>
                  )}
                  {!isVilla && rooms > 1 && ` × ${rooms} rooms`}
                  <span className="font-semibold"> = {total.toLocaleString()}</span>
                </div>
              ) : null;
            })()
          )}

          {item?.item_type === 'custom' && (
            <div className="space-y-2">
              <Label>Pricing basis</Label>
              <div className="flex gap-2">
                {(["flat", "per_person"] as const).map((b) => (
                  <Button key={b} type="button" size="sm" variant={customBasis === b ? "default" : "outline"}
                    onClick={() => {
                      setCustomBasis(b);
                      setFormData({ ...formData, quantity: b === "flat" ? "1" : String(leadPax ?? 1) });
                    }}>
                    {b === "flat" ? "Flat total" : "Per person"}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Custom item pricing info */}
          {item?.item_type === 'custom' && customBasis === 'per_person' && (
            (() => {
              const costPerUnit = parseFloat(formData.cost) || 0;
              const qty = parseInt(formData.quantity) || 1;
              const total = costPerUnit * qty;
              return qty > 0 && costPerUnit > 0 ? (
                <div className="p-3 bg-muted/50 rounded-lg text-sm">
                  <span className="font-medium">Total: </span>
                  {costPerUnit.toLocaleString()} × {qty} {qty === 1 ? 'person' : 'people'}
                  <span className="font-semibold"> = {total.toLocaleString()}</span>
                </div>
              ) : null;
            })()
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cost">
                {isAccommodation
                  ? 'Cost per Night'
                  : isMultiDayService
                    ? (formData.universal_fee_kind === 'chef' ? 'Cost per Day' : 'Cost per Person per Day')
                    : isDayBased ? 'Cost per Day'
                    : item?.item_type === 'custom' ? (customBasis === 'per_person' ? 'Price per Person' : 'Flat Total') : 'Cost per Unit'}
              </Label>
              <Input
                id="cost"
                type="number"
                step="0.01"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                disabled={isAccommodation && allocations.length > 0}
              />
              {errors.cost && <p className="text-sm text-destructive">{errors.cost}</p>}
            </div>

            {!(isMultiDayService && formData.universal_fee_kind === 'chef') && !(item?.item_type === 'custom' && customBasis === 'flat') && (
            <div className="space-y-2">
              <Label htmlFor="quantity">
                {isVilla
                  ? 'Number of Guests *'
                  : isAccommodation
                    ? 'Number of Rooms'
                    : isMultiDayService
                      ? `Number of ${formData.universal_fee_kind === 'chef' ? 'chefs' : formData.universal_fee_kind === 'guiding' ? 'guides' : formData.universal_fee_kind === 'assistant_guide' ? 'assistant guides' : 'staff'}`
                      : isDayBased
                        ? 'Number of Days'
                        : item?.item_type === 'custom' ? 'Number of People' : 'Quantity'}
              </Label>
              <Input
                id="quantity"
                disabled={isAccommodation && allocations.length > 0}
                type="number"
                min={1}
                value={isVilla ? guestCount : formData.quantity}
                onChange={(e) => {
                  if (isVilla) {
                    setGuestCount(Math.max(1, parseInt(e.target.value) || 1));
                  } else {
                    setFormData({ ...formData, quantity: e.target.value });
                  }
                }}
              />
              {isVilla ? (
                <p className="text-xs text-muted-foreground">
                  Flat private-hire rate — price does not change with guest count.
                </p>
              ) : isAccommodation && (
                <p className="text-xs text-muted-foreground">
                  Number of rooms booked
                </p>
              )}
              {errors.quantity && <p className="text-sm text-destructive">{errors.quantity}</p>}
            </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <RichTextField
              id="description"
              value={formData.description}
              onChange={(html) => setFormData((prev) => ({ ...prev, description: html }))}
            />
            <div className="flex justify-between items-center">
              {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
              <p className="text-xs text-muted-foreground ml-auto">
                {richTextLength(formData.description)} / 2500 characters
              </p>
            </div>
          </div>

          <ItemInclusionsExclusionsFields
            inclusions={inclusionsText}
            exclusions={exclusionsText}
            onInclusionsChange={setInclusionsText}
            onExclusionsChange={setExclusionsText}
            onSaveToSource={saveDefaultsTarget ? handleSaveDefaults : undefined}
            saveToSourceLabel={
              saveDefaultsTarget?.kind === "product"
                ? "Save to this experience"
                : "Save to this supplier"
            }
            isSavingToSource={savingDefaults}
          />


          <div className="space-y-2">
            <Label htmlFor="notes">Internal Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
            />
            <div className="flex justify-between items-center">
              {errors.notes && <p className="text-sm text-destructive">{errors.notes}</p>}
              <p className="text-xs text-muted-foreground ml-auto">
                {formData.notes.length} / 2500 characters
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="website_url">Website URL</Label>
            <Input
              id="website_url"
              type="url"
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              placeholder="https://example.com"
            />
            <p className="text-xs text-muted-foreground">
              Optional. Overrides the linked supplier's website on this item's itinerary card.
            </p>
          </div>


          <PricingTreatmentField
            leadId={leadId}
            currentItemId={item?.id}
            treatment={pricing}
            alternativeTo={alternativeTo}
            onChange={(t, a) => { setPricing(t); setAlternativeTo(a); }}
            linkedOption={linkedOption}
            onLinkedOptionChange={setLinkedOption}
          />

          <MarkupOverrideField
            value={markupOverride}
            onChange={setMarkupOverride}
            defaultMarkupPct={leadMarkupPct}
            netTotal={editedItemNetTotal}
          />


          <div className="flex items-start justify-between gap-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
            <div className="space-y-1">
              <Label htmlFor="is_background_cost" className="text-sm font-medium">
                Background cost
              </Label>
              <p className="text-xs text-muted-foreground">
                Hide this item from the itinerary on every document. Its cost is still included in the total. It appears as a line in the Quote PDF programme breakdown only when "Show line item breakdown" is on.
              </p>
            </div>
            <Switch
              id="is_background_cost"
              checked={formData.is_background_cost}
              onCheckedChange={(checked) => setFormData({ ...formData, is_background_cost: checked })}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
